// Default of the `contentCompare.collapseThreshold` setting, not a separate source of truth.
export const DIFF_COLLAPSE = {
	ratio: 0.5,
	longDeletionWords: 15,
	minParagraphWords: 6,
} as const;

const WORD = /[\p{L}\p{N}]+/gu;

export const wordsOf = (text: string): string[] => text.match(WORD) ?? [];

export const changedRatio = (before: string[], after: string[]): number => {
	if (!before.length && !after.length) return 0;

	const pool = new Map<string, number>();
	before.forEach((word) => pool.set(word, (pool.get(word) ?? 0) + 1));

	let common = 0;
	after.forEach((word) => {
		const left = pool.get(word);
		if (!left) return;
		pool.set(word, left - 1);
		common++;
	});

	return 1 - (2 * common) / (before.length + after.length);
};

export const shouldCollapseSentence = (
	before: string,
	after: string,
	threshold: number = DIFF_COLLAPSE.ratio,
): boolean => changedRatio(wordsOf(before), wordsOf(after)) > threshold;

// Splitting on every "." breaks on abbreviations and numbers ("fig. 1", "5.4") and flips the paragraph decision.
const SENTENCE_END = /([.!?…]+)(\s+(?=[«"'(\p{Lu}])|\s*$)/gu;

export const sentencesOf = (text: string): string[] => {
	const sentences: string[] = [];
	let start = 0;

	for (const match of text.matchAll(SENTENCE_END)) {
		const end = (match.index ?? 0) + match[1].length;
		const sentence = text.slice(start, end).trim();
		if (sentence) sentences.push(sentence);
		start = end;
	}

	const tail = text.slice(start).trim();
	if (tail) sentences.push(tail);

	return sentences;
};

export const shouldCollapseParagraph = (
	before: string,
	after: string,
	threshold: number = DIFF_COLLAPSE.ratio,
): boolean => {
	const beforeSentences = sentencesOf(before);
	const afterSentences = sentencesOf(after);

	const total = Math.max(beforeSentences.length, afterSentences.length);
	if (!total) return false;

	// Match by content, not order: one inserted sentence would shift the rest and collapse the whole paragraph.
	const takenAfter = new Set<number>();
	const unmatchedBefore: string[] = [];

	beforeSentences.forEach((sentence) => {
		const exact = afterSentences.findIndex((candidate, index) => !takenAfter.has(index) && candidate === sentence);
		if (exact >= 0) {
			takenAfter.add(exact);
			return;
		}
		unmatchedBefore.push(sentence);
	});

	let rewritten = 0;

	unmatchedBefore.forEach((sentence) => {
		let bestIndex = -1;
		// Must start above 1: a fully rewritten sentence still has to pair up, or it counts as both removed and added.
		let bestRatio = Number.POSITIVE_INFINITY;

		afterSentences.forEach((candidate, index) => {
			if (takenAfter.has(index)) return;
			const ratio = changedRatio(wordsOf(sentence), wordsOf(candidate));
			if (ratio < bestRatio) {
				bestRatio = ratio;
				bestIndex = index;
			}
		});

		if (bestIndex < 0) {
			rewritten++;
			return;
		}

		takenAfter.add(bestIndex);
		if (bestRatio > threshold) rewritten++;
	});

	rewritten += afterSentences.length - takenAfter.size;

	return rewritten / total > threshold;
};
